import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCms } from '../context/CmsContext';

export default function ReviewsSlider() {
  const { testimonials, loading: cmsLoading } = useCms();
  const [reviews, setReviews] = useState(testimonials || []);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [modalIndex, setModalIndex] = useState(null);

  // Sync testimonials from CmsContext
  useEffect(() => {
    if (testimonials && testimonials.length > 0) {
      setReviews(testimonials);
    }
  }, [testimonials]);

  // Update visible count based on window width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - visibleCount);

  // Reset index if it exceeds maxIndex due to resize
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCount, maxIndex, currentIndex]);

  // Keyboard navigation & body scroll lock for modal
  useEffect(() => {
    if (modalIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModalIndex(null);
      } else if (e.key === 'ArrowLeft') {
        setModalIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setModalIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalIndex, reviews.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === maxIndex ? 0 : prev + 1));
  };

  const handleModalPrev = () => {
    setModalIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleModalNext = () => {
    setModalIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col items-center justify-center gap-4 min-h-[300px]">
        <i className="fa-solid fa-spinner animate-spin text-brand-green text-4xl" />
        <p className="text-zinc-500 font-mono text-xs">Henter udtalelser...</p>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 relative">
      {/* Slider Container */}
      <div className="overflow-hidden py-4">
        <div
          className="flex transition-transform duration-500 ease-in-out -mx-3"
          style={{
            transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
          }}
        >
          {reviews.map((review, idx) => {
            const widthClass = 
              visibleCount === 1 
                ? 'w-full' 
                : visibleCount === 2 
                ? 'w-1/2' 
                : 'w-1/3';

            const titleText = review.title?.da || '';
            const reviewText = review.text?.da || '';
            const reviewerName = review.name?.da || '';
            const reviewerCompany = review.company?.da || '';

            // Truncate text if longer than 220 characters
            const CHARACTER_LIMIT = 220;
            const isLong = reviewText.length > CHARACTER_LIMIT;
            const displayText = isLong
              ? reviewText.slice(0, CHARACTER_LIMIT).trim() + '...'
              : reviewText;

            return (
              <div
                key={review.id || idx}
                className={`${widthClass} shrink-0 px-3`}
              >
                <div className="bg-white border border-zinc-200 hover:border-brand-green/30 rounded-2xl p-6 md:p-8 flex flex-col justify-between h-full gap-6 transition-all duration-300 hover:shadow-md">
                  {/* Quote Icon & Stars */}
                  <div className="flex items-center justify-between">
                    <span className="text-brand-green/20 text-4xl font-serif leading-none">“</span>
                    <div className="flex gap-1 text-brand-green text-xs">
                      {[...Array(review.rating || 5)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star" />
                      ))}
                    </div>
                  </div>

                  {/* Testimonial Text */}
                  <div className="flex-1 flex flex-col justify-between gap-3">
                    <div>
                      {titleText && (
                        <h4 className="text-zinc-900 font-bold text-sm md:text-base mb-2">
                          {titleText}
                        </h4>
                      )}
                      <p className="text-zinc-600 text-sm leading-relaxed italic whitespace-pre-line">
                        {displayText}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 pt-1">
                      {isLong && (
                        <div>
                          <button
                            onClick={() => setModalIndex(idx)}
                            className="text-brand-green hover:text-brand-green-dark text-xs font-bold inline-flex items-center gap-1.5 group transition-colors cursor-pointer"
                          >
                            Læs mere
                            <i className="fa-solid fa-arrow-right text-[10px] transition-transform group-hover:translate-x-0.5" />
                          </button>
                        </div>
                      )}

                      {reviewerName === 'Jacob Steen Bjørklund' && (
                        <div>
                          <a 
                            href="https://www.facebook.com/share/r/18d19TjhzG/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-green hover:text-brand-green-dark text-xs font-bold inline-flex items-center gap-1.5 group transition-colors"
                          >
                            Læs hele artiklen her
                            <i className="fa-solid fa-arrow-right text-[10px] transition-transform group-hover:translate-x-0.5" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="border-t border-zinc-200 pt-4 flex items-center gap-3">
                    <div>
                      <h4 className="text-zinc-900 text-xs md:text-sm font-bold">{reviewerName}</h4>
                      <p className="text-brand-green text-[10px] md:text-xs font-semibold uppercase tracking-wider">{reviewerCompany}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="flex justify-center md:justify-between items-center gap-4 mt-8 md:absolute md:top-1/2 md:-translate-y-1/2 md:-left-4 md:-right-4 md:mt-0 md:pointer-events-none">
        <button
          onClick={handlePrev}
          className="w-12 h-12 rounded-full bg-white border border-zinc-200 text-zinc-700 hover:text-brand-green hover:border-brand-green flex items-center justify-center transition-all duration-300 hover:shadow-md cursor-pointer md:pointer-events-auto"
          aria-label="Forrige anmeldelse"
        >
          <i className="fa-solid fa-arrow-left" />
        </button>
        <button
          onClick={handleNext}
          className="w-12 h-12 rounded-full bg-white border border-zinc-200 text-zinc-700 hover:text-brand-green hover:border-brand-green flex items-center justify-center transition-all duration-300 hover:shadow-md cursor-pointer md:pointer-events-auto"
          aria-label="Næste anmeldelse"
        >
          <i className="fa-solid fa-arrow-right" />
        </button>
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentIndex === idx ? 'w-6 bg-brand-green' : 'w-2 bg-zinc-300 hover:bg-zinc-400'
            }`}
            aria-label={`Gå til slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Review Modal Dialog */}
      {modalIndex !== null && reviews[modalIndex] && (() => {
        const activeReview = reviews[modalIndex];
        const mTitle = activeReview.title?.da || '';
        const mText = activeReview.text?.da || '';
        const mName = activeReview.name?.da || '';
        const mCompany = activeReview.company?.da || '';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs animate-fadeIn">
            {/* Backdrop click dismiss */}
            <div
              className="absolute inset-0"
              onClick={() => setModalIndex(null)}
              aria-hidden="true"
            />

            {/* Modal Box */}
            <div className="relative bg-white rounded-3xl shadow-2xl border border-zinc-100 max-w-2xl w-full max-h-[90vh] flex flex-col z-10 overflow-hidden animate-scaleIn">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-green bg-brand-green/10 px-2.5 py-1 rounded-full">
                    Anmeldelse {modalIndex + 1} af {reviews.length}
                  </span>
                </div>
                <button
                  onClick={() => setModalIndex(null)}
                  className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600 flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Luk modal"
                >
                  <i className="fa-solid fa-xmark text-lg" />
                </button>
              </div>

              {/* Modal Content Body */}
              <div className="p-6 md:p-8 overflow-y-auto flex-1 flex flex-col gap-6">
                {/* Rating & Quote */}
                <div className="flex items-center justify-between">
                  <span className="text-brand-green/20 text-5xl font-serif leading-none">“</span>
                  <div className="flex gap-1 text-brand-green text-sm">
                    {[...Array(activeReview.rating || 5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star" />
                    ))}
                  </div>
                </div>

                {/* Title & Full Text */}
                <div className="space-y-3">
                  {mTitle && (
                    <h3 className="text-zinc-900 font-bold text-lg md:text-xl">
                      {mTitle}
                    </h3>
                  )}
                  <p className="text-zinc-700 text-base leading-relaxed whitespace-pre-line italic">
                    {mText}
                  </p>
                </div>

                {/* External Link if applicable */}
                {mName === 'Jacob Steen Bjørklund' && (
                  <div className="pt-2">
                    <a
                      href="https://www.facebook.com/share/r/18d19TjhzG/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-green hover:text-brand-green-dark text-sm font-bold inline-flex items-center gap-2 group transition-colors"
                    >
                      Læs hele artiklen her
                      <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </div>
                )}

                {/* Author Info */}
                <div className="border-t border-zinc-100 pt-5 mt-auto flex items-center justify-between">
                  <div>
                    <h4 className="text-zinc-900 font-bold text-sm md:text-base">{mName}</h4>
                    {mCompany && (
                      <p className="text-brand-green text-xs font-semibold uppercase tracking-wider mt-0.5">
                        {mCompany}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer Controls (Next / Prev) */}
              <div className="px-6 py-4 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
                <button
                  onClick={handleModalPrev}
                  className="px-4 py-2.5 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-100 text-zinc-800 text-xs md:text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer shadow-xs"
                >
                  <i className="fa-solid fa-arrow-left text-xs" />
                  <span>Forrige</span>
                </button>

                <div className="text-zinc-400 text-xs font-mono hidden sm:block">
                  Brug pilene til at bladre
                </div>

                <button
                  onClick={handleModalNext}
                  className="px-4 py-2.5 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-100 text-zinc-800 text-xs md:text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer shadow-xs"
                >
                  <span>Næste</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
